import React from "react";
import { LuPackage, LuTruck, LuMapPin, LuShoppingCart, LuDollarSign, LuClipboardList } from "react-icons/lu";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";

const services = [
  { title: "Wholesale Product Supply",          body: "We carry hundreds of products across disposable vapes, kratom, delta products, glass, and novelty items — everything a convenience store or smoke shop needs, sourced from one supplier.", Icon: LuPackage },
  { title: "1–2 Day Local Delivery",            body: "Place your order and receive your stock within 1–2 business days. We service the full DFW metroplex and deliver well beyond the city into smaller towns and communities across Texas.", Icon: LuTruck },
  { title: "Wide Service Area",                 body: "We don't just cover Dallas and Fort Worth — our routes extend throughout the DFW metroplex and into surrounding regions. If you're a Texas retailer, reach out and we'll let you know if we can serve your area.", Icon: LuMapPin },
  { title: "One-Stop Shop",                     body: "Stop managing five different supplier relationships. We carry all the product categories your store needs under one roof — fewer invoices, fewer calls, and one dependable delivery.", Icon: LuShoppingCart },
  { title: "Wholesale Pricing",                 body: "Registered wholesale accounts get access to bulk pricing across all our product categories. The more you order, the better your margins. Apply online to get started.", Icon: LuDollarSign },
  { title: "Order Tracking & Account Management", body: "Log in to your wholesale account to browse the full catalog, place orders, view order history, and manage your shipping address — all from your phone or desktop.", Icon: LuClipboardList },
];

const categories = [
  { label: "Disposable Vapes", path: "/products/disposable-vapes" },
  { label: "Kratom",           path: "/products/kratom" },
  { label: "Delta Products",   path: "/products/delta" },
  { label: "Glass",            path: "/products/glass" },
  { label: "Novelty Items",    path: "/products/novelty" },
];

export default function Services() {
  return (
    <div className="min-h-screen bg-brand-surface font-sans">
      <Helmet>
        <title>Wholesale Services | Khoja Distributions — DFW, Texas</title>
        <meta name="description" content="Khoja Distributions offers wholesale product supply, 1–2 day delivery across DFW and beyond, bulk pricing, and a one-stop shop for vapes, kratom, delta, glass, and novelty items." />
        <link rel="canonical" href="https://www.khojadistributions.com/services" />
      </Helmet>

      {/* Hero */}
      <div className="bg-brand-navy py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-brand-amber/20 border border-brand-amber/30 text-brand-amber text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-6">
            What We Offer
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight tracking-tight mb-6">
            Everything Your Store Needs, Delivered Fast
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed max-w-2xl mx-auto">
            From wholesale pricing to doorstep delivery across Texas, Khoja Distributions makes restocking your shelves simple.
          </p>
        </div>
      </div>

      {/* Services grid */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <ScrollReveal key={s.title} delay={i * 0.08}>
              <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm h-full">
                <div className="w-10 h-10 rounded-xl bg-brand-navy flex items-center justify-center mb-4">
                  <s.Icon size={20} className="text-brand-amber" />
                </div>
                <h3 className="text-base font-black text-brand-navy mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {/* Product categories */}
      <div className="bg-slate-200 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <span className="text-xs font-bold text-brand-amber uppercase tracking-widest block mb-2">Product Categories</span>
            <h2 className="text-3xl font-black text-slate-900 mb-3">What We Carry</h2>
            <p className="text-slate-600 text-sm mb-10 max-w-xl mx-auto">
              We stock a wide and rotating catalog across five core product verticals — all available at wholesale pricing for registered accounts.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.12}>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((c) => (
                <Link
                  key={c.label}
                  to={c.path}
                  className="bg-white border border-slate-200 hover:border-brand-amber hover:bg-brand-amber/5 text-brand-navy font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
                >
                  {c.label}
                </Link>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* CTA */}
      <ScrollReveal>
        <div className="py-20 px-6 text-center">
          <h2 className="text-2xl font-black text-brand-navy mb-3">Ready to get started?</h2>
          <p className="text-slate-500 text-sm mb-7">Apply for a wholesale account online or give us a call — we'll get you set up fast.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" className="bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold px-7 py-3.5 rounded-xl text-sm">
              Apply for Wholesale Access
            </Link>
            <a href="tel:2146356340" className="border border-slate-200 text-brand-navy hover:bg-slate-50 font-bold px-7 py-3.5 rounded-xl text-sm transition-colors">
              Call (214) 635-6340
            </a>
          </div>
        </div>
      </ScrollReveal>
    </div>
  );
}
